import PropTypes from "prop-types";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import { definitionSelectors } from "../../modules/definition";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { typeSelectors } from "../../modules/definition/types";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { tutorialOperations, tutorialSelectors } from "../../modules/tutorial";
import { uiStateSelectors } from "../../modules/uiState";
import { tutorialDisplaySelectors } from "../../tutorial/selectors";
import { replaceTutorialPlaceholders } from "../../tutorial/utils";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import TutorialTipView from "./TutorialTipView";

const getTextWithReplacements = (text, useProps) =>
  replaceTutorialPlaceholders(
    text,
    useProps.sidebarTypes,
    useProps.orderedNarrativeTypes,
    useProps.typesById,
    useProps.defaultSequenceType,
    useProps.narrativeSectionTypes,
    useProps.syncAppName
  );

const renderTipList = props => {
  const { group } = props;
  let isActive = true;

  const tipsList = group.tips
    .map(tip => {
      const isActiveTip = isActive && !tip.isComplete;

      const showDetail = isActiveTip || props.expandedTips.indexOf(tip.id) >= 0;

      const tipRow = (
        <InspectorRow key={tip.id}>
          <div className={`tutorialListItem ${isActiveTip ? "isActive" : ""} ${tip.isComplete ? "isComplete" : ""}`}>
            <TutorialTipView
              tip={tip}
              className={null}
              isNotification={false}
              tipCompleted={tip.isComplete}
              isActive={isActive}
              activeTutorial={props.activeTutorial}
              tipIsDismissable={props.activeTipIsDismissable}
              showExpandIcon={true}
              showDetail={showDetail}
              onExpandClick={props.onToggleTipDetails}
              sidebarTypes={props.sidebarTypes}
              orderedNarrativeTypes={props.orderedNarrativeTypes}
              typesById={props.typesById}
              defaultSequenceType={props.defaultSequenceType}
              narrativeSectionTypes={props.narrativeSectionTypes}
              syncAppName={props.syncAppName}
              isEditMode={props.isEditMode}
              blockedTips={props.blockedTips}
              completedTipSet={props.completedTipSet}
              dispatchAction={props.dispatchAction}
            />
          </div>
        </InspectorRow>
      );

      if (!tip.isComplete) {
        isActive = false;
      }
      return tipRow;
    })
    .toArray();

  return <div className={`tutorialList ${group.isComplete ? "isComplete" : ""}`}>{tipsList}</div>;
};

const renderNoSelection = () => (
  <InspectorRow>
    <div className="tutorialEmpty">
      <Icon icon="statusValid" color="iconFade" />
      <span>{`Please select a tutorial from the list above`}</span>
    </div>
  </InspectorRow>
);

const renderGateMessage = message => (
  <InspectorRow>
    <div className="tutorialEmpty">
      <span>{message}</span>
    </div>
  </InspectorRow>
);

const renderCompletedMessage = props => (
  <Fragment>
    <InspectorRow>
      <div className="tutorialIntro noPadding">
        <div className="tutorialIconBlock">
          <Icon icon="tutorialIconBlock" color="iconMono" />
          <div>
            <div className="tutorialWelcome">Tutorial Complete</div>
            <div className="tutorialDescription">{getTextWithReplacements(props.group.completedMessage, props)}</div>
            <div className="tutorialStart">
              {props.isGroupGateApplicable
                ? renderGateMessage(getTextWithReplacements(props.group.gateApplicableMessage, props))
                : null}
            </div>
          </div>
        </div>
      </div>
    </InspectorRow>
  </Fragment>
);

const renderGroupPreview = props => {
  const hasCompletedTips = props.group.tips.find(tip => tip.isComplete);
  return (
    <InspectorRow>
      <div className="tutorialIntro noPadding">
        <div className="tutorialIconBlock">
          <Icon icon="tutorialGoal" color="iconMono" />
          <div>
            {renderGroupDescription(props)}
            <div className="tutorialStart">
              {props.isGroupBlocked ? (
                renderGateMessage(getTextWithReplacements(props.group.gateMessage, props))
              ) : (
                <FormButton
                  label={hasCompletedTips ? "Resume tutorial" : "Begin tutorial"}
                  onClick={() => props.onBeginTutorial(props.selectedTutorial)}
                  primary={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </InspectorRow>
  );
};

const renderGroupDescription = props => {
  return <div className="tutorialDescription">{getTextWithReplacements(props.group.description, props)}</div>;
};

const TutorialGroupView = props => {
  const isActiveTutorial = props.activeTutorial == props.selectedTutorial;
  if (!props.group) {
    return renderNoSelection();
  }

  if ((props.isGroupBlocked || !isActiveTutorial) && !props.group.isComplete) {
    return renderGroupPreview(props);
  }

  return (
    <Fragment>
      {props.group.isComplete ? (
        renderCompletedMessage(props)
      ) : (
        <div className="tutorialIconBlock">
          <Icon icon="tutorialGoal" color="iconMono" />
          <div>{renderGroupDescription(props)}</div>
        </div>
      )}
      {props.group.isComplete && props.isGroupGateApplicable
        ? renderGateMessage(getTextWithReplacements(props.group.gateApplicableMessage, props))
        : null}
      {renderTipList(props)}
    </Fragment>
  );
};

TutorialGroupView.propTypes = {
  group: PropTypes.object,
  expandedTips: PropTypes.array,
  onToggleTipDetails: PropTypes.func,
  isGroupBlocked: PropTypes.bool,
  isGroupGateApplicable: PropTypes.bool,
  blockedTips: PropTypes.object,
  completedTipSet: ImmutablePropTypes.set.isRequired,
  activeTutorial: PropTypes.string,
  activeTipIsDismissable: PropTypes.bool,
  selectedTutorial: PropTypes.string,
  dispatchAction: PropTypes.func.isRequired,
  onBeginTutorial: PropTypes.func.isRequired,
  defaultSequenceType: PropTypes.string.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  sidebarTypes: ImmutablePropTypes.list,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  narrativeSectionTypes: ImmutablePropTypes.list,
  syncAppName: PropTypes.string,
  isEditMode: PropTypes.bool,
  onNextClick: PropTypes.func
};

// Use same propTypes for all our helper functions
renderGroupDescription.propTypes = TutorialGroupView.propTypes;
renderGroupPreview.propTypes = TutorialGroupView.propTypes;
renderCompletedMessage.propTypes = TutorialGroupView.propTypes;
renderTipList.propTypes = TutorialGroupView.propTypes;

const mapStateToProps = (state, props) => {
  const group = tutorialDisplaySelectors.getTutorialDisplayStatusForGroup(state.present, props.selectedTutorial);
  const isGroupBlocked = tutorialDisplaySelectors.isGroupGateAccessRestricted(state.present, props.selectedTutorial);
  const isGroupGateApplicable = tutorialDisplaySelectors.isGroupGateApplicable(state.present, props.selectedTutorial);
  const blockedTips = tutorialDisplaySelectors.getAccessRestrictedTipsInGroup(state.present, props.selectedTutorial);
  return {
    group,
    isGroupBlocked,
    isGroupGateApplicable,
    blockedTips,
    completedTipSet: tutorialSelectors.getCompletedTips(state.present),
    activeTutorial: tutorialSelectors.getActiveTutorial(state.present),
    activeTipIsDismissable: tutorialDisplaySelectors.getTutorialActiveTipIsDismissable(state.present),
    defaultSequenceType: definitionSelectors.getDefaultSequenceType(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    sidebarTypes: typeSelectors.getSidebarTypes(state.present),
    orderedNarrativeTypes: typeSelectors.getOrderedNarrativeTypes(state.present),
    narrativeSectionTypes: narrativeSettingsSelectors.getNarrativeLabels(state.present),
    syncAppName: syncSettingsSelectors.getSyncApplicationName(state.present),
    isEditMode: uiStateSelectors.getIsEditMode(state.present)
  };
};

const readonlyActions = {};

const editingActions = {
  dispatchAction: action => action,
  onBeginTutorial: tutorialOperations.setActiveTutorial
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TutorialGroupView);
